import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, input, signal } from '@angular/core';
import {merge, of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {
  registrationPlatformNameRefusal,
  RegistrationPlatformService,
} from '@app/services/registration-platform.service';
import {AvailableRegistrationPlatform} from '@app/modules/shared/models/project-registration-platform';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RegistrationPlatformLogoComponent } from '../thirdparty-platforms-view/registration-platform-logo.component';
import { SbInputTextComponent } from '../../../_components/unified/sb-input/sb-input-text.component';
import { SbButtonComponent } from '../../../_components/unified/sb-button/sb-button.component';

/** How long typing pauses before the catalogue is searched. */
export const INSTALL_SEARCH_DEBOUNCE_MS = 300;

/** The longest install name the server takes. */
export const INSTALL_NAME_MAX = 100;

/**
 * Body component for SbModalService.open({bodyComponent, modalData: {projectId}}): searches the
 * installable platforms and installs one. A platform the project has not installed yet installs
 * straight away, under its catalogue name; another install of one it has asks for a name first
 * (the server's free name prefilled, a refused name shown on the field). It closes with the
 * installed row, so the page can open its settings. Any other failed install keeps it open, toasts
 * the server's message and reloads the list.
 */
@Component({
  selector: 'app-registration-platform-install-modal',
  templateUrl: './registration-platform-install-modal.component.html',
  styleUrls: ['./registration-platform-install-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FormsModule, RegistrationPlatformLogoComponent, SbInputTextComponent, SbButtonComponent]
})
export class RegistrationPlatformInstallModalComponent implements OnInit, OnDestroy {
  private readonly registrationPlatformService = inject(RegistrationPlatformService);
  private readonly toastr = inject(ToastrService);

  readonly projectId = input.required<number>();
  readonly closeModal = input<(result?: unknown) => void>(undefined);
  readonly dismissModal = input<(reason?: unknown) => void>(undefined);

  search = '';
  readonly platforms = signal<AvailableRegistrationPlatform[]>([]);
  readonly loading = signal(true);
  readonly loadError = signal('');
  /** The platform being installed; the other Install buttons wait for it. */
  readonly installingId = signal<number | null>(null);
  /** The installed platform being added again: the name step shows instead of the list. */
  readonly naming = signal<AvailableRegistrationPlatform | null>(null);
  name = '';
  readonly nameError = signal('');

  private readonly searches = new Subject<string>();
  /** Fetches the current search again, even when the term did not change. */
  private readonly reloads = new Subject<void>();
  private readonly destroyed = new Subject<void>();

  ngOnInit(): void {
    const terms = merge(of(''), this.searches.pipe(debounceTime(INSTALL_SEARCH_DEBOUNCE_MS))).pipe(distinctUntilChanged());
    merge(terms, this.reloads.pipe(map(() => this.search.trim())))
      .pipe(
        switchMap((term) => {
          this.loading.set(true);
          this.loadError.set('');
          return this.registrationPlatformService.getAvailablePlatforms(this.projectId(), term).pipe(
            catchError(() => {
              this.loadError.set('The platforms could not be loaded.');
              return of([] as AvailableRegistrationPlatform[]);
            }),
          );
        }),
        takeUntil(this.destroyed),
      )
      .subscribe((platforms) => {
        this.platforms.set(platforms);
        this.loading.set(false);
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get emptyText(): string {
    return this.search.trim() ? 'No platforms match your search.' : 'No platforms are available.';
  }

  /** "Installed", or "Installed ×N" for a platform installed more than once; empty when not installed. */
  installedLabel(platform: AvailableRegistrationPlatform): string {
    const count = Number(platform.installed) || 0;
    if (count < 1) {
      return '';
    }
    return count > 1 ? `Installed ×${count}` : 'Installed';
  }

  onSearch(term: string): void {
    this.search = term || '';
    this.searches.next(this.search.trim());
  }

  /** Installs a platform not installed yet; one already installed asks for the new install's name. */
  install(platform: AvailableRegistrationPlatform): void {
    if (this.installingId() !== null) {
      return;
    }
    if ((Number(platform.installed) || 0) > 0) {
      this.naming.set(platform);
      this.name = platform.suggested_name || platform.name;
      this.nameError.set('');
      return;
    }
    this.submit(platform);
  }

  onNameChange(name: string): void {
    this.name = name || '';
    this.nameError.set('');
  }

  /** Installs the platform being named, under the name typed. */
  confirmName(): void {
    if (!this.naming() || this.installingId() !== null) {
      return;
    }
    const name = this.name.trim();
    if (!name) {
      this.nameError.set('Enter a name.');
      return;
    }
    if (name.length > INSTALL_NAME_MAX) {
      this.nameError.set(`Use at most ${INSTALL_NAME_MAX} characters.`);
      return;
    }
    this.submit(this.naming(), name);
  }

  /** Leaves the name step for the list. */
  back(): void {
    if (this.installingId() !== null) {
      return;
    }
    this.naming.set(null);
    this.name = '';
    this.nameError.set('');
  }

  trackById(_index: number, platform: AvailableRegistrationPlatform): number {
    return platform.id;
  }

  private submit(platform: AvailableRegistrationPlatform, name?: string): void {
    this.installingId.set(platform.id);
    this.registrationPlatformService.installPlatform(this.projectId(), platform.id, name).subscribe({
      next: (row) => {
        this.installingId.set(null);
        this.toastr.success(`${row?.name || name || platform.name} added`);
        this.closeModal()?.(row);
      },
      error: (error) => {
        this.installingId.set(null);
        const refused = registrationPlatformNameRefusal(error);
        if (refused && this.naming()) {
          // Taken meanwhile, blank or too long: the name step stays, with the message on the field.
          this.nameError.set(refused);
          return;
        }
        this.toastr.error(error?.error?.message || 'The platform could not be added.', 'Error');
        this.naming.set(null);
        this.reloads.next();
      },
    });
  }
}
